import React, { useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  Image,
  Stack,
  Text,
  Select,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  useToast,
  Divider,
  Alert,
  AlertIcon,
  AlertDescription,
  Collapse,
  Link,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, InfoIcon } from '@chakra-ui/icons';
import AmortizationSchedule from './AmortizationSchedule';
import DisclaimerModal from './DisclaimerModal';

interface CalculatorInputs {
  purchasePrice: number;
  downPaymentPercent: number;
  downPaymentAmount: number;
  interestRate: number;
  amortizationYears: number;
  paymentFrequency: string;
  term: number;
}

interface ChangeEvent {
  target: {
    value: string;
  };
}

const MortgageCalculator: React.FC = () => {
  const toast = useToast();
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [inputs, setInputs] = useState<CalculatorInputs>({
    purchasePrice: 500000,
    downPaymentPercent: 20,
    downPaymentAmount: 100000,
    interestRate: 5.99,
    amortizationYears: 25,
    paymentFrequency: 'monthly',
    term: 5,
  });

  const [results, setResults] = useState({
    monthlyPayment: 0,
    totalMortgage: 0,
    cmhcInsurance: 0,
    principalPayment: 0,
    interestPayment: 0,
  });

  const calculateDownPaymentAmount = (price: number, percent: number) => {
    return (price * percent) / 100;
  };

  const calculateDownPaymentPercent = (price: number, amount: number) => {
    return (amount / price) * 100;
  };

  const calculateCMHCInsurance = (price: number, downPaymentPercent: number) => {
    if (downPaymentPercent >= 20) return 0;
    
    let premium = 0;
    if (downPaymentPercent >= 15) premium = 2.4;
    else if (downPaymentPercent >= 10) premium = 2.8;
    else if (downPaymentPercent >= 5) premium = 4;
    
    return (price * (premium / 100));
  };

  const calculateMortgage = () => {
    const principal = inputs.purchasePrice - inputs.downPaymentAmount;
    const cmhcInsurance = calculateCMHCInsurance(inputs.purchasePrice, inputs.downPaymentPercent);
    const totalMortgage = principal + cmhcInsurance;
    
    // Monthly interest rate
    const monthlyRate = (inputs.interestRate / 100) / 12;
    // Total number of payments
    const numberOfPayments = inputs.amortizationYears * 12;
    
    // Monthly payment calculation using the mortgage payment formula
    const monthlyPayment = totalMortgage * 
      (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
      (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    
    // First month's interest payment
    const interestPayment = totalMortgage * monthlyRate;
    // First month's principal payment
    const principalPayment = monthlyPayment - interestPayment;

    // Force a re-render by creating a new object
    setResults({
      monthlyPayment,
      totalMortgage,
      cmhcInsurance,
      principalPayment,
      interestPayment,
    });

    toast({
      title: "Calculation Complete",
      description: "Your mortgage details have been updated.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const handlePriceChange = (value: string) => {
    const price = parseFloat(value) || 0;
    const downPaymentAmount = calculateDownPaymentAmount(price, inputs.downPaymentPercent);
    setInputs(prev => ({
      ...prev,
      purchasePrice: price,
      downPaymentAmount,
    }));
  };

  const handleDownPaymentPercentChange = (value: string) => {
    const percent = parseFloat(value) || 0;
    const amount = calculateDownPaymentAmount(inputs.purchasePrice, percent);
    setInputs(prev => ({
      ...prev,
      downPaymentPercent: percent,
      downPaymentAmount: amount,
    }));
  };

  const handleDownPaymentAmountChange = (value: string) => {
    const amount = parseFloat(value) || 0;
    const percent = calculateDownPaymentPercent(inputs.purchasePrice, amount);
    setInputs(prev => ({
      ...prev,
      downPaymentAmount: amount,
      downPaymentPercent: percent,
    }));
  };

  return (
    <Box maxW="1200px" mx="auto" p={8}>
      <Stack direction="column" spacing={8}>
        <Box textAlign="center">
          <Box position="relative" display="inline-block" mb={1}>
            <Image 
              src="/maple-leaf.png" 
              alt="Canadian Maple Leaf" 
              width="40px"
              height="40px"
              mx="auto"
              style={{ filter: 'brightness(1)' }}
            />
          </Box>
          <Heading color="deebits.red">Canadian Mortgage Calculator</Heading>
          <Text color="gray.600" mt={2}>Calculate your mortgage payments with confidence</Text>
        </Box>

        <Grid templateColumns="repeat(2, 1fr)" gap={8}>
          <GridItem>
            <Stack direction="column" spacing={4}>
              <FormControl>
                <FormLabel>Purchase Price ($)</FormLabel>
                <NumberInput
                  min={0}
                  value={inputs.purchasePrice}
                  onChange={handlePriceChange}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Down Payment</FormLabel>
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <NumberInput
                    value={inputs.downPaymentPercent}
                    onChange={handleDownPaymentPercentChange}
                    min={5}
                    max={100}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <NumberInput
                    value={inputs.downPaymentAmount}
                    onChange={handleDownPaymentAmountChange}
                    min={0}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                </Grid>
              </FormControl>

              <FormControl>
                <FormLabel>Interest Rate (%)</FormLabel>
                <NumberInput
                  min={0}
                  max={20}
                  step={0.01}
                  value={inputs.interestRate}
                  onChange={(value) => setInputs(prev => ({ ...prev, interestRate: parseFloat(value) || 0 }))}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>

              <FormControl>
                <FormLabel>Amortization Period (Years)</FormLabel>
                <Select
                  value={inputs.amortizationYears}
                  onChange={(e: ChangeEvent) => setInputs(prev => ({ ...prev, amortizationYears: parseInt(e.target.value) }))}
                >
                  {[...Array(30)].map((_, i) => (
                    <option key={i + 1} value={i + 1}>{i + 1} years</option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Payment Frequency</FormLabel>
                <Select
                  value={inputs.paymentFrequency}
                  onChange={(e: ChangeEvent) => setInputs(prev => ({ ...prev, paymentFrequency: e.target.value }))}
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="accelerated_biweekly">Accelerated Bi-Weekly</option>
                </Select>
              </FormControl>

              <Box mt={2}>
                <HStack spacing={2} mb={4} color="gray.600" fontSize="sm" justify="center">
                  <Text>By clicking Calculate, you agree that results are estimates only</Text>
                  <DisclaimerModal />
                </HStack>
                <Button 
                  onClick={calculateMortgage}
                  size="lg"
                  width="100%"
                  colorScheme="deebits"
                >
                  Calculate Mortgage
                </Button>
              </Box>
            </Stack>
          </GridItem>

          <GridItem>
            <Box 
              bg="white" 
              p={6} 
              borderRadius="lg" 
              boxShadow="lg"
              border="2px"
              borderColor="deebits.red"
            >
              <Stack direction="column" spacing={4}>
                <Heading size="md" color="deebits.red">Mortgage Summary</Heading>
                
                <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                  <Text fontWeight="bold">Monthly Payment:</Text>
                  <Text>${results.monthlyPayment.toFixed(2)}</Text>
                  
                  <Text fontWeight="bold">Total Mortgage:</Text>
                  <Text>${results.totalMortgage.toFixed(2)}</Text>
                  
                  <Text fontWeight="bold">CMHC Insurance:</Text>
                  <Text>${results.cmhcInsurance.toFixed(2)}</Text>
                  
                  <Text fontWeight="bold">Principal Payment:</Text>
                  <Text>${results.principalPayment.toFixed(2)}</Text>
                  
                  <Text fontWeight="bold">Interest Payment:</Text>
                  <Text>${results.interestPayment.toFixed(2)}</Text>
                </Grid>
              </Stack>
            </Box>
          </GridItem>
        </Grid>

        {results.monthlyPayment > 0 && (
          <AmortizationSchedule
            loanAmount={inputs.purchasePrice - inputs.downPaymentAmount}
            interestRate={inputs.interestRate}
            amortizationYears={inputs.amortizationYears}
            paymentFrequency={inputs.paymentFrequency}
            monthlyPayment={results.monthlyPayment}
          />
        )}
      </Stack>
    </Box>
  );
};

export default MortgageCalculator; 